import React, { useState } from 'react';

import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import cs from 'classnames';

/**
 * @type {import('react').FC<import('./lib/types').DragDropListProps>}
 */

const DragAndDropList = ({
  items,
  onDragEnd,
  disabledItems = [],
  renderItem,
  droppableId = 'droppable',
  direction = 'vertical',
  className = '',
  parentClassName = ''
}) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const { source, destination } = result;

    const sourceIsDisabled = disabledItems.includes(items[source.index].field);
    if (sourceIsDisabled) return;

    const finalItems = Array.from(items);
    const [moved] = finalItems.splice(source.index, 1);
    finalItems.splice(destination.index, 0, moved);

    const disabledIndicesBefore = items
      .map((item, index) => (disabledItems.includes(item.field) ? index : null))
      .filter((index) => index !== null);

    const disabledIndicesAfter = finalItems
      .map((item, index) => (disabledItems.includes(item.field) ? index : null))
      .filter((index) => index !== null);

    const disabledPositionsChanged = disabledIndicesBefore.some(
      (beforeIndex, i) => beforeIndex !== disabledIndicesAfter[i]
    );

    if (disabledPositionsChanged) {
      const originalDisabledIndex = disabledIndicesBefore.find(
        (index) => index === destination.index
      );

      if (originalDisabledIndex !== undefined) {
        const correctedDestinationIndex = originalDisabledIndex > 0 ? originalDisabledIndex - 1 : 0;

        const correctedItems = Array.from(items);
        const [reMoved] = correctedItems.splice(source.index, 1);

        const finalCorrectedIndex = Math.min(correctedDestinationIndex, correctedItems.length);

        correctedItems.splice(finalCorrectedIndex, 0, reMoved);

        onDragEnd(correctedItems);
        return;
      }

      return;
    }

    onDragEnd(finalItems);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId={droppableId} direction={direction}>
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef} className={parentClassName}>
            {items.map((item, index) => (
              <Draggable
                key={item.id || item.key || index}
                draggableId={item.id?.toString() || index.toString()}
                index={index}
                isDragDisabled={disabledItems.includes(item.field)}>
                {(provided) => (
                  <DraggableItem
                    provided={provided}
                    item={item}
                    index={index}
                    className={className}
                    renderItem={renderItem}
                  />
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default DragAndDropList;

// dnd swallows mousedown, which blocks embedded controls (react-select) from
// opening. `setDisableDragging` (passed to `renderItem`) drops the drag handle
// over an interactive region so clicks land; the rest of the row still drags.
const DraggableItem = ({ provided, item, index, className, renderItem }) => {
  const [disableDragging, setDisableDragging] = useState(false);

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...(disableDragging ? {} : provided.dragHandleProps)}
      className={cs('flex items-center justify-between gap-2 py-1', className)}>
      {renderItem(item, index, setDisableDragging)}
    </div>
  );
};
